import { useEffect, useState } from "react";
import { CommerceDialog } from "../commerce/CommerceDialog";
import { Btn } from "../ui/Btn";
import { Inp } from "../ui/Inp";
import {C, withAlpha } from "../../constants/theme";
import { buscarFotosMakerWorld } from "./makerworldApi";
import { GradeFotosMakerWorld } from "./GradeFotosMakerWorld";
import { BotaoColarLink } from "./BotaoColarLink";
import { useColarLink } from "../../hooks/useColarLink";
import { placasDoPerfil, duracaoLegivel } from "./dadosDoPerfil";

const gramas = (g) => (Number.isFinite(g) ? `${Math.round(g)} g` : "—");
const titulo = { color:C.muted, fontSize:12, fontWeight:600, letterSpacing:1, margin:"18px 0 8px" };

// Modal "Importar do MakerWorld" da Calculadora:
//   1) cola o link -> backend devolve título, fotos, perfis de impressão e licença;
//   2) escolhe o PERFIL (é o "arquivo" do MakerWorld: vários por modelo), as
//      placas e as fotos;
//   3) `onConfirmar` recebe tudo pronto — quem chama preenche a calculadora e
//      guarda link/fotos pro produto. Este componente não grava nada.
// O 3MF em si o MakerWorld só libera pra quem está logado lá, então o modal
// leva o usuário até o perfil escolhido pra baixar (e depois "Importar 3MF").
// `soFotos`: versão enxuta dos pop-ups de registrar produto / orçamento — só o
// link e as fotos, sem perfil/placas (não mexe no tempo e nos filamentos do cálculo).
export function ImportarMakerWorldCalculadora({ onFechar, onConfirmar, linkInicial = "", soFotos = false }) {
  const [link, setLink] = useState(linkInicial);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState("");
  const [modelo, setModelo] = useState(null);
  const [perfilId, setPerfilId] = useState(null);
  const [placasMarcadas, setPlacasMarcadas] = useState(() => new Set());
  const [fotosMarcadas, setFotosMarcadas] = useState(() => new Set());

  // Ctrl+V vale enquanto o modal está na etapa do link (este componente só é
  // montado com o modal aberto).
  const colar = useColarLink(
    (valor) => { setLink(valor); setErro(""); },
    { ativo: !modelo && !buscando },
  );

  const perfil = modelo?.perfis.find(p => p.id === perfilId) || null;

  const escolherPerfil = (p) => { setPerfilId(p.id); setPlacasMarcadas(new Set(p.placas.map(x => x.index))); };

  const buscar = async () => {
    if (!link.trim() || buscando) return;
    setBuscando(true); setErro("");
    try {
      const r = await buscarFotosMakerWorld(link.trim());
      setModelo(r);
      setFotosMarcadas(new Set(r.images.map(i => i.id)));
      const sugerido = soFotos ? null : (r.perfis.find(p => p.id === r.perfilSugerido) || r.perfis[0]);
      if (sugerido) escolherPerfil(sugerido); else { setPerfilId(null); setPlacasMarcadas(new Set()); }
    } catch (e) { setErro(e.message); }
    setBuscando(false);
  };

  // Link já preenchido no campo de quem abriu: vai direto pra escolha das fotos.
  useEffect(() => { if (/^https?:\/\/\S+$/i.test(linkInicial.trim())) buscar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const alternar = (setter) => (chave) => setter(atual => {
    const novo = new Set(atual);
    novo.has(chave) ? novo.delete(chave) : novo.add(chave);
    return novo;
  });

  const placas = perfil ? placasDoPerfil(perfil, [...placasMarcadas]) : [];
  const totalSeg = placas.reduce((s, p) => s + (Number.isFinite(p.predictionSeconds) ? p.predictionSeconds : 0), 0);
  const totalPeso = placas.reduce((s, p) => s + (Number.isFinite(p.weightG) ? p.weightG : 0), 0);
  const semPlacaEscolhida = !!perfil && perfil.placas.length > 0 && placas.length === 0;

  const confirmar = () => {
    if (semPlacaEscolhida) { setErro("Selecione pelo menos uma placa."); return; }
    onConfirmar({
      modelo, perfil, placas,
      // Mantém a ordem do MakerWorld (capa primeiro), não a ordem dos cliques.
      fotosIds: modelo.images.filter(i => fotosMarcadas.has(i.id)).map(i => i.id),
    });
  };

  return (
    <CommerceDialog
      title="Importar do MakerWorld"
      subtitle={modelo ? modelo.title : soFotos ? "Cole o link do modelo: pegamos as fotos." : "Cole o link do modelo: pegamos as fotos e os dados do perfil de impressão."}
      onClose={onFechar}
      footer={modelo ? (
        <>
          <Btn outline color={C.muted} onClick={onFechar}>Cancelar</Btn>
          <Btn color={C.accent2} onClick={confirmar} disabled={semPlacaEscolhida}>Importar</Btn>
        </>
      ) : (
        <>
          <Btn outline color={C.muted} onClick={onFechar}>Cancelar</Btn>
          <Btn color={C.accent2} onClick={buscar} disabled={buscando || !link.trim()}>{buscando ? "⏳ Buscando…" : "Buscar"}</Btn>
        </>
      )}>
      {!modelo ? (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <div style={{ flex:1, minWidth:0 }}>
              <Inp label="LINK DO MODELO" value={link} placeholder="https://makerworld.com/pt/models/…"
                onChange={e => { setLink(e.target.value); setErro(""); }}
                onKeyDown={e => { if (e.key === "Enter") buscar(); }} />
            </div>
            <BotaoColarLink colar={colar} desabilitado={buscando} style={{ minHeight:38 }} />
          </div>
          <p className="commerce-hint" style={{ margin:0 }}>Copiou o link? Ctrl+V aqui já cola. O link fica salvo. As fotos escolhidas são copiadas para o seu produto e continuam funcionando mesmo que o MakerWorld mude ou remova o modelo.</p>
        </div>
      ) : (
        <div>
          {!soFotos && <div style={{ ...titulo, marginTop:0 }}>PERFIL DE IMPRESSÃO {modelo.perfis.length > 1 ? `(${modelo.perfis.length} disponíveis)` : ""}</div>}
          {soFotos ? null : modelo.perfis.length === 0 ? (
            <p className="commerce-hint" style={{ margin:0 }}>Este modelo não traz dados de impressão. Vamos guardar o link e as fotos; preencha o tempo e os filamentos na calculadora.</p>
          ) : (
            <div role="radiogroup" aria-label="Perfil de impressão" style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {modelo.perfis.map(p => (
                <label key={p.id} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 12px", borderRadius:10, cursor:"pointer",
                  border:"1.5px solid " + (p.id === perfilId ? C.accent2 : C.border), background:p.id === perfilId ? withAlpha(C.accent2, "12") : "transparent" }}>
                  <input type="radio" name="perfil-makerworld" checked={p.id === perfilId} onChange={() => escolherPerfil(p)} style={{ marginTop:3, accentColor:C.accent2 }} />
                  <span style={{ minWidth:0, flex:1 }}>
                    <span style={{ display:"block", fontWeight:700, fontSize:13.5, overflowWrap:"anywhere" }}>{p.titulo}{p.padrao ? " · padrão" : ""}</span>
                    <span style={{ display:"block", color:C.muted, fontSize:12, marginTop:2 }}>
                      {duracaoLegivel(p.segundos)} · {gramas(p.pesoG)} · {p.placas.length || 1} placa(s) · {p.filamentos.length} cor(es)
                    </span>
                    <span style={{ display:"flex", gap:4, marginTop:6, flexWrap:"wrap" }}>
                      {p.filamentos.map((f, i) => (
                        <span key={i} title={`${f.tipo || "Filamento"} ${f.cor || ""} — ${gramas(f.gramas)}`}
                          style={{ width:16, height:16, borderRadius:"50%", background:f.cor || C.border, border:"1px solid " + C.border }} />
                      ))}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {perfil && perfil.placas.length > 1 && (
            <>
              <div style={titulo}>PLACAS DESTE PERFIL</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {perfil.placas.map(p => (
                  <label key={p.index} style={{ display:"flex", gap:8, alignItems:"center", fontSize:13, cursor:"pointer" }}>
                    <input type="checkbox" checked={placasMarcadas.has(p.index)} onChange={() => alternar(setPlacasMarcadas)(p.index)} style={{ accentColor:C.accent2 }} />
                    <span>Placa {p.index}</span>
                    <span style={{ color:C.muted, fontSize:12 }}>{duracaoLegivel(p.segundos)} · {gramas(p.pesoG)}</span>
                  </label>
                ))}
              </div>
              <p className="commerce-hint">Selecionado: {duracaoLegivel(totalSeg)} · {gramas(totalPeso)}</p>
            </>
          )}

          <div style={soFotos ? { ...titulo, marginTop:0 } : titulo}>FOTOS</div>
          <GradeFotosMakerWorld imagens={modelo.images} marcadas={fotosMarcadas} onAlternar={alternar(setFotosMarcadas)} />
          <div style={{ marginTop:8 }}>
            <button type="button" onClick={() => setFotosMarcadas(fotosMarcadas.size === modelo.images.length ? new Set() : new Set(modelo.images.map(i => i.id)))}
              style={{ background:"none", border:"none", color:C.muted, fontSize:12, textDecoration:"underline", cursor:"pointer", padding:0 }}>
              {fotosMarcadas.size === modelo.images.length ? "Limpar seleção" : "Selecionar todas"} ({fotosMarcadas.size}/{modelo.images.length})
            </button>
          </div>

          {!soFotos && (
            <>
              <div style={titulo}>ARQUIVO 3MF</div>
              <p className="commerce-hint" style={{ margin:0 }}>
                O MakerWorld só libera o download do 3MF para quem está logado nele, então o arquivo não vem automaticamente.
                {perfil ? " Abra o perfil escolhido, baixe o 3MF e depois use “Importar 3MF/GCODE” aqui na calculadora — o link e as fotos ficam guardados." : ""}
              </p>
              {perfil && (
                <a href={perfil.urlPagina} target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-block", marginTop:8, color:C.accent2, fontSize:13, fontWeight:700 }}>
                  Abrir este perfil no MakerWorld ↗
                </a>
              )}
            </>
          )}
        </div>
      )}
      {erro && <p role="alert" style={{ margin:"12px 0 0", color:C.danger, fontSize:13 }}>{erro}</p>}
    </CommerceDialog>
  );
}
